import { DISCOUNT_PRESETS_MINOR, DISCOUNT_REASONS, TIP_METHODS, type DiscountReason } from '@360face/domain';
import type { ExtrasDraft, ExtrasResult } from './payment-extras';
import type { TabletMessages } from './messages';

/**
 * Payment screen, under "Payment received" (owner 15.10.2026): tip (typed by the therapist, e.g. left
 * on the card terminal), then one "Change amount" button → Discount (quick amounts or typed, with a
 * required reason) or Upgrade (the therapist types the final amount instead of the bill).
 */
export function PaymentExtras({
  draft,
  onChange,
  result,
  billMinor,
  t,
  money,
  tipOnly,
}: {
  draft: ExtrasDraft;
  onChange: (d: ExtrasDraft) => void;
  result: ExtrasResult;
  billMinor: number;
  t: TabletMessages;
  money: (minor: number) => string;
  tipOnly?: boolean;
}) {
  const set = (patch: Partial<ExtrasDraft>) => onChange({ ...draft, ...patch });
  const euros = (v: string) => v.replace(/[^\d.,]/g, '').slice(0, 7);
  const hasDiscount = result.discountMinor > 0;
  const problem = result.problem;

  return (
    <div className="t-extras">
      <fieldset>
        <legend>{t.tipTitle}</legend>
        <p className="t-hint">{t.tipHint}</p>
        <div className="t-extras-row">
          <label className="t-pin">
            {t.tipAmount}
            <input
              inputMode="decimal"
              autoComplete="off"
              value={draft.tipText}
              onChange={(e) => set({ tipText: euros(e.target.value) })}
            />
          </label>
          <div className="t-filter">
            {TIP_METHODS.map((m) => (
              <button
                key={m}
                className={m === draft.tipMethod ? 'on' : ''}
                aria-pressed={m === draft.tipMethod}
                onClick={() => set({ tipMethod: m })}
              >
                {t.methods[m]}
              </button>
            ))}
          </div>
        </div>
        {problem === 'tip_amount' && <p className="t-error">{t.errors.default}</p>}
      </fieldset>

      {!tipOnly && draft.adjust === 'none' && (
        <button className="t-extras-open" onClick={() => set({ adjust: 'discount' })}>
          {t.changeAmount}
        </button>
      )}

      {!tipOnly && draft.adjust !== 'none' && (
        <fieldset>
          <legend>{t.changeAmount}</legend>
          <div className="t-seg t-extras-kind">
            {(['discount', 'upgrade'] as const).map((k) => (
              <button
                key={k}
                className={`k-${k}${draft.adjust === k ? ' on' : ''}`}
                aria-pressed={draft.adjust === k}
                onClick={() => set({ adjust: k })}
              >
                {k === 'discount' ? t.adjustDiscount : t.adjustUpgrade}
              </button>
            ))}
            <button
              onClick={() =>
                set({
                  adjust: 'none',
                  discountPreset: undefined,
                  discountText: '',
                  reason: '',
                  note: '',
                  finalText: '',
                })
              }
            >
              ✕ {t.adjustNone}
            </button>
          </div>

          {draft.adjust === 'upgrade' && (
            <>
              <p className="t-hint">{t.upgradeHint}</p>
              <label className="t-pin">
                {t.finalAmount}
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  autoFocus
                  className={problem === 'final_amount' && draft.finalText ? 'missing' : ''}
                  value={draft.finalText}
                  onChange={(e) => set({ finalText: euros(e.target.value) })}
                />
              </label>
            </>
          )}

          {draft.adjust === 'discount' && (
            <>
              <div className="t-filter">
                {DISCOUNT_PRESETS_MINOR.filter((p) => p <= billMinor).map((p) => (
                  <button
                    key={p}
                    className={`k-discount-preset${draft.discountPreset === p ? ' on' : ''}`}
                    aria-pressed={draft.discountPreset === p}
                    onClick={() => set({ discountPreset: p, discountText: '' })}
                  >
                    −{money(p)}
                  </button>
                ))}
              </div>
              <div className="t-extras-row">
                <label className="t-pin">
                  {t.discountAmount}
                  <input
                    inputMode="decimal"
                    autoComplete="off"
                    value={draft.discountText}
                    onChange={(e) => set({ discountText: euros(e.target.value), discountPreset: undefined })}
                  />
                </label>
                <label className="t-pin">
                  {t.discountReason}
                  <select
                    value={draft.reason}
                    className={problem === 'reason' ? 'missing' : ''}
                    onChange={(e) => set({ reason: e.target.value as DiscountReason | '' })}
                  >
                    <option value="">{t.discountReasonPick}</option>
                    {DISCOUNT_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {t.reasons[r]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {hasDiscount && (
                <label className="t-pin t-extras-note">
                  {draft.reason === 'other' ? t.discountNoteRequired : t.discountNote}
                  <input
                    autoComplete="off"
                    maxLength={200}
                    className={problem === 'note' ? 'missing' : ''}
                    value={draft.note}
                    onChange={(e) => set({ note: e.target.value })}
                  />
                </label>
              )}
              {problem === 'discount_too_high' && <p className="t-error">{t.discountTooHigh}</p>}
              {problem === 'discount_amount' && <p className="t-error">{t.errors.default}</p>}
            </>
          )}
        </fieldset>
      )}

      {(hasDiscount || result.finalMinor !== undefined || result.tipMinor > 0) && (
        <p className="t-extras-sum" role="status">
          {t.payBill} {money(billMinor)}
          {hasDiscount && (
            <>
              {' '}
              − {money(result.discountMinor)} = {t.payDue} <strong>{money(result.dueMinor)}</strong>
            </>
          )}
          {result.finalMinor !== undefined && (
            <>
              {' '}
              → {t.payFinal} <strong>{money(result.finalMinor)}</strong>
            </>
          )}
          {result.tipMinor > 0 && (
            <>
              {' '}
              + {t.payTip} {money(result.tipMinor)} → {t.payCharge} <strong>{money(result.chargeMinor)}</strong>
            </>
          )}
        </p>
      )}
    </div>
  );
}
